import * as React from 'react'
import { CheckIcon } from 'lucide-react'
import { cn } from '../../../lib/utils'

/**
 * Chat event card — represents a scheduled meeting or call inside a chat thread.
 * Shape matches the ChatMessage bubble, but with a leading icon + title + optional subtitle.
 * Example in Figma: "Встреча Пт, 23 января, 10:00 / Москва, ул Попова 13, 18".
 */
export interface ChatEventCardProps extends React.HTMLAttributes<HTMLDivElement> {
	icon: React.ReactNode
	title: string
	subtitle?: string
	time?: string
	readStatus?: 'sent' | 'read' | null
	align?: 'start' | 'end'
}

const ChatEventCard = React.forwardRef<HTMLDivElement, ChatEventCardProps>(
	({ className, icon, title, subtitle, time, readStatus, align = 'end', ...props }, ref) => (
		<div
			ref={ref}
			data-slot="chat-event-card"
			className={cn(
				'flex w-fit max-w-[85%] items-start gap-3 rounded-(--radius-md) px-3 py-2',
				align === 'end'
					? 'self-end bg-(--color-brand-light)'
					: 'self-start bg-(--color-muted)',
				className
			)}
			{...props}
		>
			<div className="mt-0.5 flex size-6 shrink-0 items-center justify-center text-(--color-info)">
				{icon}
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<p className="text-sm leading-5 text-(--color-foreground)">{title}</p>
				{subtitle && (
					<p className="text-sm leading-5 text-(--color-muted-foreground)">
						{subtitle}
					</p>
				)}
				<div className="flex items-center justify-end gap-1 text-xs leading-4 text-(--color-muted-foreground)">
					{time && <span>{time}</span>}
					{readStatus && (
						<span className="flex items-center">
							<CheckIcon className="size-3" />
							{readStatus === 'read' && <CheckIcon className="-ml-1.5 size-3" />}
						</span>
					)}
				</div>
			</div>
		</div>
	)
)

ChatEventCard.displayName = 'ChatEventCard'
export { ChatEventCard }
